/**
 * DipSitesController — effect-only child component for dip-site overlays.
 *
 * Extracted from MapView.tsx as part of the Claude audit 2026-05-18
 * finding 7 part 2 (component split, wave 4). Owns its own narrow zustand
 * subscriptions so its effects only re-run when dip-site-related state
 * changes, not on every MapView render.
 *
 * Returns null — no DOM output.
 */
import { useEffect } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useDipSiteStore } from '../../stores/dipSiteStore';
import { useAuthStore } from '../../stores/authStore';
import { buildDipSitesFC } from '../dipSitesLayer';
import { getMapInstance, safeGetSource } from '../MapView';
import { registerStyleRebuild } from '../layerVisibilityRegistry';
import React from 'react';

// HI-1 step 3 (2026-05-23): rebuildRef prop removed. The controller now
// registers its style-rebuild restore with layerVisibilityRegistry.
export function DipSitesController() {
  const showDipSites = useLayerVisibilityStore((s) => s.layers.dipSites);
  // (Claude audit 2026-05-18 finding 6) Content-keyed signature of the
  // dip-site list so the effect only rebuilds when a relevant field
  // actually changes.
  const dipSitesKey = useDipSiteStore((s) =>
    s.list
      .map((d) => `${d.id}:${d.latitude}:${d.longitude}:${d.ownershipClass}`)
      .join('|'),
  );
  const refreshDipSites   = useDipSiteStore((s) => s.refresh);
  const subscribeDipSites = useDipSiteStore((s) => s.subscribe);
  const userId = useAuthStore((s) => s.user?.id ?? null);

  // Dip sites — in-memory store (no fetch overlay; the store carries
  // the list). Refresh on first toggle-on; subscribe to realtime so
  // any other pilot in the org adding a site updates this map too.
  useEffect(() => {
    function apply() {
      const map = getMapInstance();
      if (!map || !map.getLayer('dip-sites-circle')) return;
      const v = showDipSites ? 'visible' : 'none';
      map.setLayoutProperty('dip-sites-circle', 'visibility', v);
      map.setLayoutProperty('dip-sites-label',  'visibility', v);
      if (!showDipSites) return;
      const fc = buildDipSitesFC(useDipSiteStore.getState().list);
      const src = safeGetSource<GeoJSONSource>(map, 'dip-sites');
      if (src) src.setData(fc as any);
    }
    // HI-1 step 2/3: style-rebuild restore registered with the central
    // registry.
    const unregister = registerStyleRebuild({
      id: 'dipSites',
      restore: () => apply(),
    });
    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { unregister(); };
  }, [showDipSites, dipSitesKey]);

  // First-load fetch + realtime subscription for the dip-sites store.
  // Gated on a signed-in session: dip_sites is in the `app` schema with
  // RLS that requires auth.uid() to be non-null. Hitting it as anon
  // returns 401 'permission denied for table dip_sites' (PostgREST
  // 42501) and clutters the console for every unauthenticated viewer.
  // Auth restore from localStorage happens async on mount, so we wait
  // for `user` to flip non-null before kicking the first fetch.
  useEffect(() => {
    if (!userId) return;
    void refreshDipSites();
    const unsub = subscribeDipSites();
    return () => unsub();
  }, [userId, refreshDipSites, subscribeDipSites]);

  return null;
}
